import React, { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';

import {
  makeRemoveSessionUseCase,
  makeSyncUserSessionUseCase,
} from 'modules/authentication/domain/useCases';
import { FullPageLoading } from 'shared/presentation/components/molecules';

import { AuthenticationContext, IAuthenticationContextData } from '../../hooks';

const DefaultAuthenticationProvider: React.FC = ({ children }) => {
  const [session, setSession] =
    useState<IAuthenticationContextData['session']>(null);
  const [initializing, setInitializing] = useState(true);
  const [loggedAsGuest, setLoggedAsGuest] = useState(false);
  const [guestId, setGuestId] =
    useState<IAuthenticationContextData['guestId']>(null);

  const client = useQueryClient();

  useEffect(() => {
    makeSyncUserSessionUseCase()
      .execute()
      .then(session => {
        setSession(session);
        setGuestId(session?.id || null);
        setInitializing(false);
      });
  }, []);

  const logout = useCallback(async () => {
    await makeRemoveSessionUseCase().execute();

    setSession(null);
    setGuestId(null);
    setLoggedAsGuest(false);

    client.invalidateQueries({
      inactive: true,
    });
  }, [client]);

  const login = useCallback<IAuthenticationContextData['login']>(session => {
    setSession(session);
    setGuestId(session.id);
  }, []);

  const guestLogin = useCallback<IAuthenticationContextData['guestLogin']>(
    id => {
      setGuestId(id);
      setLoggedAsGuest(true);
    },
    [],
  );

  if (initializing) return <FullPageLoading />;

  return (
    <AuthenticationContext.Provider
      value={{
        session,
        guestId,
        loggedAsGuest,
        login,
        guestLogin,
        logout,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default DefaultAuthenticationProvider;
